import { InfoCircleOutlined } from "@ant-design/icons";
import { Procedure } from "../../../../types/procedures";
import { procedureDisplayName } from "../../../../utils/procedureDisplayName";
import {
  trackQuoteProcedureRemoved,
  trackQuoteProcedureSelected,
} from "../../../../utils/quoteAnalytics";
import {
  procedureIsFeatured,
  procedureIsSpecial,
  specialBadgeColorKey,
  specialBadgeText,
} from "./specialBadge";
// @ts-ignore
import { useTranslation } from "react-i18next";

type ProcedureCardProps = {
  procedure: Procedure;
  isSelected: boolean;
  isDisabled: boolean;
  removeSelectedProcedure: (procedure: Procedure) => void;
  addSelectedProcedure: (procedure: Procedure) => void;
  onClickInfoCircle: (e: any) => void;
  imageUrl?: string;
  selectedCount: number;
};

const ProcedureCard = ({
  procedure,
  isDisabled,
  isSelected,
  onClickInfoCircle,
  addSelectedProcedure,
  removeSelectedProcedure,
  imageUrl,
  selectedCount,
}: ProcedureCardProps) => {
  // @ts-ignore
  const { t, i18n } = useTranslation("procedures");

  // Prefer the patient-facing marketing name; otherwise the translated name.
  const procedureName = procedureDisplayName(procedure, {
    language: i18n?.language,
    t,
  });

  const typeLabel = (procedure.procedureTypes || [])
    .map((pt: any) =>
      t(`types.${pt.name}`, { defaultValue: pt.name }),
    )
    .join(" · ");

  // Out-of-window procedures are filtered out of the list upstream (QuoteProvider),
  // so a rendered SPECIAL or FEATURED always shows its pill.
  const isSpecial = procedureIsSpecial(procedure);
  const isFeatured = !isSpecial && procedureIsFeatured(procedure);
  const badgeColorKey = specialBadgeColorKey(procedure.specialBadgeColor);
  const badgeText = specialBadgeText(
    procedure,
    i18n?.language,
    t(isSpecial ? "common.limitedTimeOffer" : "common.featured"),
  );
  const procedureTypeIds = (procedure.procedureTypes || []).map(
    (pt: any) => pt.id,
  );

  return (
    <div
      key={procedure.id}
      className={`procedure-card ${isSelected ? "selected" : ""} ${
        isDisabled ? "disabled" : ""
      } ${isSpecial ? "special" : ""} ${isFeatured ? "featured" : ""}`}
      onClick={() => {
        if (isDisabled) {
          return;
        }

        if (isSelected) {
          removeSelectedProcedure(procedure);
          trackQuoteProcedureRemoved({
            procedureId: procedure.id,
            procedureTypeIds,
            selectedCountAfter: Math.max(0, selectedCount - 1),
            surface: "desktop",
          });
          return;
        }
        addSelectedProcedure(procedure);
        trackQuoteProcedureSelected({
          procedureId: procedure.id,
          procedureTypeIds,
          selectedCountAfter: selectedCount + 1,
          surface: "desktop",
        });
      }}
    >
      <div className="procedure-image">
        <img
          src={
            imageUrl
              ? imageUrl
              : `${process.env.PUBLIC_URL}/img/app-logo.png`
          }
          alt={procedureName}
          loading="lazy"
          decoding="async"
        />
        {(isSpecial || isFeatured) && (
          <div
            className={`procedure-special-banner procedure-special-banner--${badgeColorKey}`}
          >
            {badgeText}
          </div>
        )}
      </div>

      {procedure.description && procedure.description !== "" && (
        <InfoCircleOutlined
          className="info-modal-opener"
          onClick={onClickInfoCircle}
        />
      )}

      <div className="procedure-card-content">
        {typeLabel && (
          <div className="procedure-type-breadcrumb">{typeLabel}</div>
        )}
        <span className="procedure-card-title">{procedureName}</span>
        <span className="procedure-select-chip">
          {isSelected
            ? t("common.selected", { defaultValue: "Selected" })
            : t("common.select", { defaultValue: "Select" })}
        </span>
      </div>
    </div>
  );
};

export default ProcedureCard;
